<script setup lang="ts">
import {computed, ref} from "vue";
import {YearMonth} from "./marktberichte/Types.ts";
import {DatePickerMonths as months} from "./marktberichte/Globals.ts";
import { onMounted, onBeforeUnmount } from 'vue';
import {getNewestAndOldest, showLogin} from "../modules/marktberichte.ts";

const props = defineProps<{
  onDateUpdate: (yearmonth:YearMonth ) => void,
  noFuture?: boolean,
  noPast?: boolean,
  min?: YearMonth,
  max?: YearMonth
}>();


const initDate = new Date();
const currentDate = ref<YearMonth>({
  year: initDate.getFullYear(),
  month: initDate.getMonth()
});

const isOpen = ref(false);

const inputVal = ref('');

function togglePicker() {
  if (showLogin()) return
  isOpen.value = !isOpen.value;
}

function getOldestDate(){
  let dates_berichte = getNewestAndOldest()
  let oldest = dates_berichte[1]
  if (!oldest) return {month:0, year: 0}
  let oldestDate = new Date(oldest)
  return{month:oldestDate.getMonth(),year:oldestDate.getFullYear()}
}

const selectableMonths = computed<[number, boolean][]>(() => {
  const now = initDate;
  let max:YearMonth = {month:12,year:99999};
  if(props.noFuture){
    max = {month:now.getMonth(),year: now.getFullYear()};
  }
  if(props.max){
    max = props.max;
  }
  let min:YearMonth = getOldestDate();
  if(props.noPast){
    min = {month:now.getMonth(),year: now.getFullYear()};
  }
  if(props.min){
    min = props.min;
  }
  return months.map((_, index) => {
    const isInPastRange = currentDate.value.year > min.year ? true : currentDate.value.year >= min.year && index >= min.month;
    const isInFutureRange = currentDate.value.year < max.year ? true : currentDate.value.year <= max.year && index <= max.month;
    const isSelectable = isInPastRange && isInFutureRange;
    return [index, isSelectable];
  });
});

const canNext = computed(() => {
  if (props.noFuture) {
    if (currentDate.value.year >= initDate.getFullYear()) {
      return false;
    }
  }

  if (props.max != null) {
    if ((currentDate.value.year + 1) > props.max.year) {
      return false;
    }
  }

  return true;
});

const canPrev = computed(() => {
  if (props.noPast) {
    if (currentDate.value.year <= initDate.getFullYear()) {
      return false;
    }
  }

  if (props.min != null) {
    if ((currentDate.value.year - 1) < props.min.year) {
      return false;
    }
  }

  if((currentDate.value.year - 1) < getOldestDate().year){
    return false
  }
  return true;
});


function prev() {
  if(!canPrev.value){
    return;
  }
  currentDate.value.year-=1;
}

function next() {
  if (!canNext.value) {
    return;
  }
  currentDate.value.year++;
}

function setDate(yearmonth: YearMonth){
  inputVal.value = `${months[yearmonth.month]} ${yearmonth.year}`;
  props.onDateUpdate(yearmonth);
  isOpen.value = false;
}

function setNow(){
  setDate({
    month:initDate.getMonth(),
    year:initDate.getFullYear()
  });
}

const datePickerRef = ref<HTMLElement | null>(null);

onMounted(() => {
  function handleClickOutside(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (datePickerRef.value && !datePickerRef.value.contains(target)) {
      if (isOpen.value) {
        isOpen.value = false;
      }
    }
  }

  document.body.addEventListener("click", handleClickOutside);

  onBeforeUnmount(() => {
    document.body.removeEventListener("click", handleClickOutside);
  });
});

defineExpose({
  setPickerDateNow: setNow,
  setPickerDate: setDate
});
</script>

<template>
  <div class="marktberichte__date__input" ref="datePickerRef" @click="togglePicker">
    <input type="text" v-model="inputVal" style="pointer-events: none">
    <div class="marktberichte__date__picker" v-if="isOpen" @click.stop.prevent>
      <div class="marktberichte__date__picker__prev" @click="prev" :disabled="!canPrev">{{ currentDate.year - 1 }}</div>
      <div class="marktberichte__date__picker__curr">{{ currentDate.year }}</div>
      <div class="marktberichte__date__picker__next" @click="next" :disabled="!canNext">{{ currentDate.year + 1 }}</div>
      <div class="marktberichte__date__picker__months">
        <template v-for="[index, isSelectable] in selectableMonths">
          <div :disabled="!isSelectable" @click="setDate({month: index, year: currentDate.year})">{{ months[index] }}</div>
        </template>

      </div>
    </div>
  </div>
</template>

<style scoped>

</style>